import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Typography, Button, Box, TextField } from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import DailyThroughputChart from "../components/DailyThroughputChart";
import DateSelector from "../components/DateSelector";
import FullWidthLayout from "../components/FullWidthLayout";
import { fetchAllWorkcells } from "../api";
import WorkcellDailyTurnoverChart from "../components/WorkcellDailyTurnoverChart";
import WorkcellDailyPlannedVsDoneChart from "../components/WorkcellDailyPlannedVsDoneChart";
import WorkcellDailyCalendar from "../components/WorkcellDailyCalendar";
import { useTranslation } from "react-i18next";
import { formatLocalDate } from "../utils/localDateTime";

const WorkcellDailyOverviewPage = () => {
  const { date, workcell_id } = useParams();
  const navigate = useNavigate();
  const [workcells, setWorkcells] = useState([]);
  const [loadingWorkcells, setLoadingWorkcells] = useState(true);
  const [turnoverLoading, setTurnoverLoading] = useState(true);
  const [throughputLoading, setThroughputLoading] = useState(true);
  const [plannedVsDoneLoading, setPlannedVsDoneLoading] = useState(true);
  const { t } = useTranslation();

  // Default to today if no date is provided
  const selectedDate = date || formatLocalDate(new Date());

  // Reset loading states when date or workcell changes
  useEffect(() => {
    setTurnoverLoading(true);
    setThroughputLoading(true);
    setPlannedVsDoneLoading(true);
  }, [selectedDate, workcell_id]);

  // Fetch workcells for selector
  useEffect(() => {
    const fetchWorkcells = async () => {
      try {
        const data = await fetchAllWorkcells();
        const filtered = data.filter(w => w.has_oee);
        setWorkcells(filtered);
      } finally {
        setLoadingWorkcells(false);
      }
    };
    fetchWorkcells();
  }, []);

  // Navigate only if the URL doesn't match
  useEffect(() => {
    if (!date || date !== selectedDate) {
      navigate(`/chart/workcell/${workcell_id}/daily/${selectedDate}`, { replace: true });
    }
  }, [date, selectedDate, workcell_id, navigate]);

  const handleShowMonthlyWorkcellPage = useCallback(() => {
    navigate(`/chart/workcell/${workcell_id}/monthly/${selectedDate}`);
  }, [navigate, workcell_id, selectedDate]);

  const handleDateChange = useCallback((newDate) => {
    navigate(`/chart/workcell/${workcell_id}/daily/${newDate}`);
  }, [navigate, workcell_id]);

  // Check if any data is still loading
  const isAnyLoading = turnoverLoading || throughputLoading || plannedVsDoneLoading;

  return (
    <FullWidthLayout date={selectedDate}>
      <Box display="flex" alignItems="center" mb={2} mt={1} style={{ position: "relative" }}>
        <Autocomplete
          sx={{ minWidth: 260, mr: 2 }}
          size="small"
          disabled={loadingWorkcells}
          options={workcells}
          getOptionLabel={(option) => option.workcell_name || ''}
          isOptionEqualToValue={(option, value) => option.workcell_id === value.workcell_id}
          value={workcells.find(w => w.workcell_id === parseInt(workcell_id)) || null}
          onChange={(event, newValue) => {
            if (newValue && newValue.workcell_id) {
              navigate(`/chart/workcell/${newValue.workcell_id}/daily/${selectedDate}`);
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label={t("workcell.selector.label")} />
          )}
        />

        <Button variant="contained" onClick={handleShowMonthlyWorkcellPage} sx={{ mt: 2 }} style={{ position: 'absolute', right: 0, top: 0, marginTop: "0" }}>
          {t("workcell.dailyOverview.backToMonthly")}
        </Button>
      </Box>

      <Typography textAlign="center" gutterBottom>
        {t("workcell.dailyOverview.intro")}
      </Typography>

      <DateSelector date={selectedDate} setDate={handleDateChange} disabled={isAnyLoading} />

      <Typography variant="h4" gutterBottom mt={5}>{t("workcell.dailyOverview.turnover.title")}</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t("workcell.dailyOverview.turnover.description")}
        </Typography>
      <WorkcellDailyTurnoverChart
        date={selectedDate}
        workcellId={workcell_id}
        loading={turnoverLoading}
        onLoadingChange={setTurnoverLoading}
      />

      <Typography variant="h4" gutterBottom>
        {t("workcell.dailyOverview.throughput.title")}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {t("workcell.dailyOverview.throughput.description")}
      </Typography>
      <DailyThroughputChart
        date={selectedDate}
        workcellId={workcell_id}
        loading={throughputLoading}
        onLoadingChange={setThroughputLoading}
      />

      <Typography variant="h4" gutterBottom>
        {t("workcell.dailyOverview.plannedVsDone.title")}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {t("workcell.dailyOverview.plannedVsDone.description")}
      </Typography>
      <WorkcellDailyPlannedVsDoneChart
        date={selectedDate}
        workcellId={workcell_id}
        loading={plannedVsDoneLoading}
        onLoadingChange={setPlannedVsDoneLoading}
      />

      {/* Workcell Activity Calendar (Day View) */}
      <WorkcellDailyCalendar workcellId={workcell_id} date={selectedDate} />
    </FullWidthLayout>
  );
};

export default WorkcellDailyOverviewPage;
